"use client";
import { Flex } from "@aembre/ui/flex";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@aembre/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  FormContainer,
  FormControlInputWidget,
  FormControlTextareaWidget,
  FormErrorMessage,
  FormFieldHeadingLabelText,
  FormFlexContainer,
  FormHeader,
  FormSubmit,
  FormSuccessMessage,
} from "@/components/form-utils";
import { supabase } from "@/lib/supabase";
import { useContactFormSchema } from "../schema";

export function ContactUsForm({ className }: { className?: string }) {
  const tableSubmission = "contact_submissions";

  const schema = useContactFormSchema();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);

  const defaultValues = {
    message: "",
    name: "",
    company: "",
    email: "",
    phone: "",
  };

  const form = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Form
      {...form}
      fieldOrder={["message", "name", "company", "email", "phone"]}
    >
      <FormContainer
        className={className}
        onSubmit={form.handleSubmit(async (data) => {
          try {
            const { error } = await supabase.from(tableSubmission).insert({
              name: data.name,
              company: data.company,
              email: data.email,
              phone: data.phone,
              message: data.message,
            });

            if (error) throw error;

            // success
            form.reset(defaultValues);
            setIsSubmitted(true);
          } catch (error) {
            console.error("error => ", error);
            setIsError(true);
          }
        })}
      >
        <FormFlexContainer className="gap-3">
          {/* Heading */}
          <FormHeader
            heading="Let's discuss your needs!"
            subheading="Do you have a question for our specialists, want to discuss your
              project, or learn more about our expertise? Feel free to send your
              request using the form below!"
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => {
              return (
                <FormItem className="w-full">
                  <FormLabel>
                    <FormFieldHeadingLabelText label="How can we help" />
                  </FormLabel>

                  <FormControl>
                    <FormControlTextareaWidget
                      {...field}
                      placeholder="Kindly describe your request."
                      disabled={form.formState.isSubmitting}
                      autoFocus={true}
                    />
                  </FormControl>

                  <FormMessage className="text-primary-300" />
                </FormItem>
              );
            }}
          />

          <Flex
            direction={{
              base: "col",
              md: "row",
            }}
            align={"center"}
            justify={"center"}
            className="w-full gap-3"
          >
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => {
                return (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormFieldHeadingLabelText label="Full Name" />
                    </FormLabel>

                    <FormControl>
                      <FormControlInputWidget
                        {...field}
                        type="text"
                        placeholder="Enter full name"
                        disabled={form.formState.isSubmitting}
                        autoFocus={false}
                      />
                    </FormControl>

                    <FormMessage className="text-primary-300" />
                  </FormItem>
                );
              }}
            />

            {/* Company Name */}
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => {
                return (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormFieldHeadingLabelText label="Company" />
                    </FormLabel>

                    <FormControl>
                      <FormControlInputWidget
                        {...field}
                        type="text"
                        placeholder="Enter company name"
                        disabled={form.formState.isSubmitting}
                        autoFocus={false}
                      />
                    </FormControl>

                    <FormMessage className="text-primary-300" />
                  </FormItem>
                );
              }}
            />
          </Flex>

          <Flex
            direction={{
              base: "col",
              md: "row",
            }}
            align={"center"}
            justify={"center"}
            className="w-full gap-3"
          >
            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => {
                return (
                  <FormItem className="relative w-full">
                    <FormLabel>
                      <FormFieldHeadingLabelText label="Work Email" />
                    </FormLabel>

                    <FormControl>
                      <FormControlInputWidget
                        {...field}
                        type="email"
                        placeholder="Work Email"
                        disabled={form.formState.isSubmitting}
                        autoFocus={false}
                      />
                    </FormControl>

                    <FormMessage className="text-primary-300" />
                  </FormItem>
                );
              }}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => {
                return (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormFieldHeadingLabelText label="Phone" />
                    </FormLabel>

                    <FormControl>
                      <FormControlInputWidget
                        {...field}
                        type="tel"
                        placeholder="Enter phone number"
                        disabled={form.formState.isSubmitting}
                        autoFocus={false}
                      />
                    </FormControl>

                    <FormMessage className="text-primary-300" />
                  </FormItem>
                );
              }}
            />
          </Flex>

          {/* submit */}
          <FormSubmit className="self-center">Send →</FormSubmit>

          {/* Success message */}
          <div className="min-h-9 w-full">
            {isSubmitted && (
              <FormSuccessMessage onDismiss={() => setIsSubmitted(false)} />
            )}

            {/* Error message */}
            {isError && (
              <FormErrorMessage onDismiss={() => setIsError(false)} />
            )}
          </div>
        </FormFlexContainer>
      </FormContainer>
    </Form>
  );
}
